import {
  pgTable,
  text,
  boolean,
  timestamp,
  integer,
  index,
  uniqueIndex,
  foreignKey,
  jsonb,
} from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import {
  translationSegmentPolicy,
  translationVoiceMode,
  translationViewerMode,
  translationOriginalMix,
  translationStartPolicy,
  translationSessionStatus,
  translationVoiceProfileKind,
  translationVoiceProvider,
} from './enums';
import { livestream } from './livestream';
import { user } from './user';
import { file } from './files';
import { adminUser } from './admin';

/**
 * Real-time перевод эфиров: STT → MT → TTS.
 * Канон: docs/livestream-translation-architecture.md §7.
 *
 * Изоляция тенантов в этом репо — отдельная БД на тенанта (`dbUrl` + ALS
 * `runWithTenant`), поэтому колонки `tenantId` здесь нет и быть не должно:
 * строки уже живут в базе своего тенанта.
 */

/**
 * Термин глоссария: как переводить конкретное слово/фразу на каждый язык.
 * Хранится как `{ source, targets: { [lang]: string } }`.
 */
export interface TranslationGlossaryTerm {
  source: string;
  targets: Record<string, string>;
  caseSensitive?: boolean;
}

/**
 * Снимок метрик сессии на момент завершения (или последний известный).
 * Пишется сервисом сессии, читается аналитикой — форма свободная.
 */
export interface TranslationSessionMetrics {
  lagMsP50?: number;
  lagMsP95?: number;
  sttSeconds?: number;
  mtChars?: number;
  ttsChars?: number;
  costEstimate?: number;
  laneCount?: number;
}

/**
 * TranslationGlossary — словарь терминов тенанта (имена, продукты, термины),
 * который подставляется в MT, чтобы «Мастерс Клуб» не переводился дословно.
 */
export const translationGlossary = pgTable(
  'TranslationGlossary',
  {
    id: integer().primaryKey().generatedAlwaysAsIdentity(),

    name: text().notNull(),
    description: text(),

    // [{ source, targets: { uz: '...', en: '...' } }, ...]
    terms: jsonb().$type<TranslationGlossaryTerm[]>().default([]).notNull(),

    isActive: boolean().default(true).notNull(),

    createdAt: timestamp({ precision: 3, mode: 'date' })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
    updatedAt: timestamp({ precision: 3, mode: 'date' })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
  },
  (table) => [index('TranslationGlossary_isActive_idx').using('btree', table.isActive.asc().nullsLast())],
);

/**
 * TranslationVoiceProfile — библиотека голосов тенанта.
 *
 * Голос выбирает администратор; модель STT/TTS не знает людей по именам и
 * не выбирает `providerVoiceId` сама (см. канон §7A.0). Клон требует
 * зафиксированного согласия — `consentRecordedAt`.
 */
export const translationVoiceProfile = pgTable(
  'TranslationVoiceProfile',
  {
    id: integer().primaryKey().generatedAlwaysAsIdentity(),

    // Человекочитаемое имя в админке: «Ведущий Иван», «Neutral RU-UZ».
    name: text().notNull(),

    kind: translationVoiceProfileKind().default('PRESET').notNull(),
    provider: translationVoiceProvider().default('ELEVENLABS').notNull(),

    // voice_id на стороне провайдера — то, что уходит в TTS-запрос.
    providerVoiceId: text().notNull(),

    // Исходный сэмпл клона (для аудита/пересоздания). Файл может быть удалён
    // независимо от профиля, поэтому set null.
    sampleFileId: integer(),

    // Подсказки по языкам, на которых голос звучит хорошо (не ограничение).
    languageHints: text().array().default(sql`ARRAY[]::text[]`).notNull(),

    // Согласие на клонирование голоса. Пока NULL — профиль kind=CLONE
    // не должен использоваться в проде (проверка на уровне сервиса).
    consentRecordedAt: timestamp({ precision: 3, mode: 'date' }),

    // Архивный профиль не предлагается в выпадающих списках, но остаётся
    // валидным для уже настроенных эфиров.
    archivedAt: timestamp({ precision: 3, mode: 'date' }),

    createdAt: timestamp({ precision: 3, mode: 'date' })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
    updatedAt: timestamp({ precision: 3, mode: 'date' })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
  },
  (table) => [
    index('TranslationVoiceProfile_kind_idx').using('btree', table.kind.asc().nullsLast()),
    // Активные (не архивные) профили для селектов админки.
    index('TranslationVoiceProfile_archivedAt_idx').using('btree', table.archivedAt.asc().nullsLast()),
    foreignKey({
      columns: [table.sampleFileId],
      foreignColumns: [file.id],
      name: 'TranslationVoiceProfile_sampleFileId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('set null'),
  ],
);

/**
 * LivestreamTranslationConfig — настройки перевода конкретного эфира
 * (таб «Перевод в реальном времени» в админке).
 *
 * ВАЖНО: `targetLanguages` — это языки перевода (codes из справочника
 * `Language`). Их нельзя путать с `Livestream.languages`, который является
 * фильтром доступа: запись туда языка перевода скрыла бы эфир от всех
 * остальных зрителей.
 */
export const livestreamTranslationConfig = pgTable(
  'LivestreamTranslationConfig',
  {
    id: integer().primaryKey().generatedAlwaysAsIdentity(),

    livestreamId: integer().notNull(),

    // Тумблер таба. false — пайплайн не стартует вообще.
    enabled: boolean().default(false).notNull(),

    // Язык речи на эфире. AUTO оставлен на будущее — определение языка
    // источника моделью; сейчас продукт ожидает PINNED.
    sourceLanguage: text().default('ru').notNull(),
    sourceLanguageAuto: boolean().default(false).notNull(),

    // Языки перевода — codes из `Language` (isActive). Multi-select админки.
    targetLanguages: text().array().default(sql`ARRAY[]::text[]`).notNull(),

    // Что зритель получает по умолчанию, если сам ещё ничего не выбирал.
    defaultViewerMode: translationViewerMode().default('DUB_PLUS_CAPTIONS').notNull(),

    // Дефолтный микс громкостей (0–100) для режима MIX.
    defaultOriginalVolume: integer().default(0).notNull(),
    defaultTranslationVolume: integer().default(100).notNull(),

    policy: translationSegmentPolicy().default('BALANCED').notNull(),

    voiceMode: translationVoiceMode().default('SINGLE').notNull(),

    // Голос для SINGLE. NULL → используется neutral.
    defaultVoiceProfileId: integer(),
    // Фолбэк для незнакомого/низкоуверенного спикера и при ошибках провайдера.
    neutralVoiceProfileId: integer(),

    // NULL → берётся default из интеграции ElevenLabs.
    sttModelId: text(),
    ttsModelId: text(),
    // Модель перевода текста (OpenRouter). NULL → бесплатная по умолчанию.
    mtModelId: text(),

    glossaryId: integer(),

    // Судьба оригинальной дорожки под озвучкой.
    originalUnderDub: translationOriginalMix().default('MUTE').notNull(),
    // Только для DUCK_DB (Web Audio), отрицательное значение в dB.
    duckingDb: integer(),

    captionsEnabled: boolean().default(true).notNull(),
    dubEnabled: boolean().default(false).notNull(),

    // Показывать субтитры оригинала ОДНОВРЕМЕННО с переводом (двойные
    // субтитры). Физически работает только если язык оригинала входит в
    // targetLanguages — иначе лейн-«расшифровка» не запущен и писать
    // субтитры оригинала некому (см. resolveViewerSettings в сервисе).
    dualCaptionsEnabled: boolean().default(false).notNull(),

    startPolicy: translationStartPolicy().default('ON_LIVE').notNull(),

    // Верхняя граница числа одновременных языковых лейнов на эфир —
    // защита от неожиданной стоимости и нагрузки на аплинк.
    maxConcurrentLanes: integer().default(3).notNull(),

    // Включается только если voiceMode=PER_SPEAKER И диаризация в realtime
    // подтверждена (см. docs/elevenlabs.md, gate P3).
    diarizationEnabled: boolean().default(false).notNull(),

    createdAt: timestamp({ precision: 3, mode: 'date' })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
    updatedAt: timestamp({ precision: 3, mode: 'date' })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
  },
  (table) => [
    // Один конфиг на эфир.
    uniqueIndex('LivestreamTranslationConfig_livestreamId_key').using(
      'btree',
      table.livestreamId.asc().nullsLast(),
    ),
    index('LivestreamTranslationConfig_enabled_idx').using('btree', table.enabled.asc().nullsLast()),
    foreignKey({
      columns: [table.livestreamId],
      foreignColumns: [livestream.id],
      name: 'LivestreamTranslationConfig_livestreamId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('cascade'),
    // Удаление голоса не должно ронять конфиг — эфир падает в neutral.
    foreignKey({
      columns: [table.defaultVoiceProfileId],
      foreignColumns: [translationVoiceProfile.id],
      name: 'LivestreamTranslationConfig_defaultVoiceProfileId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('set null'),
    foreignKey({
      columns: [table.neutralVoiceProfileId],
      foreignColumns: [translationVoiceProfile.id],
      name: 'LivestreamTranslationConfig_neutralVoiceProfileId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('set null'),
    foreignKey({
      columns: [table.glossaryId],
      foreignColumns: [translationGlossary.id],
      name: 'LivestreamTranslationConfig_glossaryId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('set null'),
  ],
);

/**
 * LivestreamSpeakerSlot — «роль» говорящего на эфире (ведущий, гость 1…)
 * и назначенный ей голос.
 *
 * `diarizationLabel` связывает анонимную метку STT (`speaker_0`) со слотом.
 * Модель не знает, что это Иван — связь задаёт оператор.
 */
export const livestreamSpeakerSlot = pgTable(
  'LivestreamSpeakerSlot',
  {
    id: integer().primaryKey().generatedAlwaysAsIdentity(),

    livestreamId: integer().notNull(),

    // Стабильный ключ слота: 'host', 'guest_1', … Используется в Redis-карте
    // голосов и в hot-swap API.
    slotKey: text().notNull(),
    displayName: text().notNull(),

    // NULL → сегменты этого слота озвучиваются нейтральным голосом.
    voiceProfileId: integer(),

    // Метка диаризации STT, если она уже привязана к слоту.
    diarizationLabel: text(),

    // Слот по умолчанию: его голос берётся в режиме SINGLE.
    isPrimary: boolean().default(false).notNull(),

    sortOrder: integer().default(0).notNull(),

    createdAt: timestamp({ precision: 3, mode: 'date' })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
    updatedAt: timestamp({ precision: 3, mode: 'date' })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
  },
  (table) => [
    uniqueIndex('LivestreamSpeakerSlot_livestream_slotKey_key').using(
      'btree',
      table.livestreamId.asc().nullsLast(),
      table.slotKey.asc().nullsLast(),
    ),
    // Одна метка диаризации не может указывать на два слота одновременно,
    // иначе речь одного человека озвучивалась бы двумя голосами.
    uniqueIndex('LivestreamSpeakerSlot_livestream_diarizationLabel_key')
      .using('btree', table.livestreamId.asc().nullsLast(), table.diarizationLabel.asc().nullsLast())
      .where(sql`"diarizationLabel" IS NOT NULL`),
    foreignKey({
      columns: [table.livestreamId],
      foreignColumns: [livestream.id],
      name: 'LivestreamSpeakerSlot_livestreamId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('cascade'),
    foreignKey({
      columns: [table.voiceProfileId],
      foreignColumns: [translationVoiceProfile.id],
      name: 'LivestreamSpeakerSlot_voiceProfileId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('set null'),
  ],
);

/**
 * LivestreamTranslationSession — один запуск пайплайна перевода на эфире.
 *
 * Живёт независимо от статуса самого эфира: падение перевода не должно
 * влиять на оригинальный playback (инвариант канона §1).
 */
export const livestreamTranslationSession = pgTable(
  'LivestreamTranslationSession',
  {
    id: integer().primaryKey().generatedAlwaysAsIdentity(),

    livestreamId: integer().notNull(),

    status: translationSessionStatus().default('STARTING').notNull(),

    startedAt: timestamp({ precision: 3, mode: 'date' }),
    endedAt: timestamp({ precision: 3, mode: 'date' }),

    // Последняя ошибка для админ-панели (провайдер отвалился, нет ключа…).
    lastError: text(),

    // Ревизия карты голосов, с которой работает воркер. Инкремент при
    // hot-swap; воркеры перечитывают карту, увидев больший номер.
    voiceMapRevision: integer().default(0).notNull(),

    metricsSnapshot: jsonb().$type<TranslationSessionMetrics>(),

    createdAt: timestamp({ precision: 3, mode: 'date' })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
    updatedAt: timestamp({ precision: 3, mode: 'date' })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
  },
  (table) => [
    index('LivestreamTranslationSession_livestreamId_idx').using(
      'btree',
      table.livestreamId.asc().nullsLast(),
    ),
    // Одна живая сессия на эфир: повторный старт должен переиспользовать
    // существующую, а не плодить параллельные пайплайны.
    uniqueIndex('LivestreamTranslationSession_livestream_active_key')
      .using('btree', table.livestreamId.asc().nullsLast())
      .where(sql`"endedAt" IS NULL`),
    index('LivestreamTranslationSession_status_idx').using('btree', table.status.asc().nullsLast()),
    foreignKey({
      columns: [table.livestreamId],
      foreignColumns: [livestream.id],
      name: 'LivestreamTranslationSession_livestreamId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('cascade'),
  ],
);

/**
 * UserLivestreamTranslationPreference — выбор зрителя: язык, режим,
 * громкости дорожек.
 *
 * `livestreamId IS NULL` — глобальная настройка пользователя для всех эфиров;
 * строка с конкретным `livestreamId` переопределяет её для одного эфира.
 */
export const userLivestreamTranslationPreference = pgTable(
  'UserLivestreamTranslationPreference',
  {
    id: integer().primaryKey().generatedAlwaysAsIdentity(),

    userId: integer().notNull(),

    // NULL = глобальный дефолт пользователя.
    livestreamId: integer(),

    // NULL = смотреть оригинал.
    language: text(),

    mode: translationViewerMode().default('ORIGINAL').notNull(),

    // Субтитры переключаются независимо от дорожки звука.
    captionsEnabled: boolean().default(true).notNull(),

    // Громкости 0–100 для режима MIX (и как override в остальных).
    originalVolume: integer().default(0).notNull(),
    translationVolume: integer().default(100).notNull(),

    createdAt: timestamp({ precision: 3, mode: 'date' })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
    updatedAt: timestamp({ precision: 3, mode: 'date' })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
  },
  (table) => [
    // Одна настройка на пару (юзер, эфир).
    uniqueIndex('UserLivestreamTranslationPreference_user_livestream_key').using(
      'btree',
      table.userId.asc().nullsLast(),
      table.livestreamId.asc().nullsLast(),
    ),
    // Глобальная настройка пользователя — ровно одна.
    uniqueIndex('UserLivestreamTranslationPreference_user_global_key')
      .using('btree', table.userId.asc().nullsLast())
      .where(sql`"livestreamId" IS NULL`),
    foreignKey({
      columns: [table.userId],
      foreignColumns: [user.id],
      name: 'UserLivestreamTranslationPreference_userId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('cascade'),
    foreignKey({
      columns: [table.livestreamId],
      foreignColumns: [livestream.id],
      name: 'UserLivestreamTranslationPreference_livestreamId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('cascade'),
  ],
);

/**
 * TranslationUsageEvent — потребление провайдеров за окно времени.
 * Основа для стоимости эфира и алертов по бюджету.
 */
export const translationUsageEvent = pgTable(
  'TranslationUsageEvent',
  {
    id: integer().primaryKey().generatedAlwaysAsIdentity(),

    livestreamId: integer().notNull(),
    sessionId: integer(),

    // Язык лейна; NULL для общих затрат эфира (например, STT источника).
    language: text(),

    sttSeconds: integer().default(0).notNull(),
    mtChars: integer().default(0).notNull(),
    ttsChars: integer().default(0).notNull(),

    provider: text(),
    modelId: text(),

    // Оценка в минимальных единицах валюты (сотые), чтобы не хранить float.
    costEstimateMinor: integer().default(0).notNull(),

    windowStart: timestamp({ precision: 3, mode: 'date' }).notNull(),
    windowEnd: timestamp({ precision: 3, mode: 'date' }).notNull(),

    createdAt: timestamp({ precision: 3, mode: 'date' })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
  },
  (table) => [
    index('TranslationUsageEvent_livestream_window_idx').using(
      'btree',
      table.livestreamId.asc().nullsLast(),
      table.windowStart.asc().nullsLast(),
    ),
    index('TranslationUsageEvent_sessionId_idx').using('btree', table.sessionId.asc().nullsLast()),
    foreignKey({
      columns: [table.livestreamId],
      foreignColumns: [livestream.id],
      name: 'TranslationUsageEvent_livestreamId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('cascade'),
    // Сессия может быть подчищена ретеншеном — счётчики расхода остаются.
    foreignKey({
      columns: [table.sessionId],
      foreignColumns: [livestreamTranslationSession.id],
      name: 'TranslationUsageEvent_sessionId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('set null'),
  ],
);

/**
 * LivestreamVoiceMapAudit — кто и когда менял голос в эфире.
 *
 * Смена голоса «в моменте» — действие с продуктовыми последствиями
 * (зритель слышит другого человека), поэтому фиксируется отдельно от
 * общего audit-лога, вместе с ревизией карты.
 */
export const livestreamVoiceMapAudit = pgTable(
  'LivestreamVoiceMapAudit',
  {
    id: integer().primaryKey().generatedAlwaysAsIdentity(),

    livestreamId: integer().notNull(),
    sessionId: integer(),

    // Кто применил изменение.
    adminId: integer(),

    // Что менялось: слот или глобальный голос (NULL для global/mode change).
    slotKey: text(),

    oldVoiceProfileId: integer(),
    newVoiceProfileId: integer(),

    // Режим до и после — для случая переключения SINGLE ↔ PER_SPEAKER.
    oldVoiceMode: translationVoiceMode(),
    newVoiceMode: translationVoiceMode(),

    // Ревизия карты после применения — с ней сверяются воркеры.
    revision: integer().notNull(),

    createdAt: timestamp({ precision: 3, mode: 'date' })
      .default(sql`CURRENT_TIMESTAMP`)
      .notNull(),
  },
  (table) => [
    index('LivestreamVoiceMapAudit_livestream_createdAt_idx').using(
      'btree',
      table.livestreamId.asc().nullsLast(),
      table.createdAt.desc().nullsLast(),
    ),
    foreignKey({
      columns: [table.livestreamId],
      foreignColumns: [livestream.id],
      name: 'LivestreamVoiceMapAudit_livestreamId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('cascade'),
    foreignKey({
      columns: [table.sessionId],
      foreignColumns: [livestreamTranslationSession.id],
      name: 'LivestreamVoiceMapAudit_sessionId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('set null'),
    // Аудит переживает удаление админа.
    foreignKey({
      columns: [table.adminId],
      foreignColumns: [adminUser.id],
      name: 'LivestreamVoiceMapAudit_adminId_fkey',
    })
      .onUpdate('cascade')
      .onDelete('set null'),
  ],
);
